import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Disclosure } from "@headlessui/react";
import LoadingOverlay from "react-loading-overlay";
import { useBreadcrumb } from "hooks/useBreadcrumb";

import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import PanelOne from "components/Registration/PanelOne";
import PanelTwo from "components/Registration/PanelTwo";
import PanelThree from "components/Registration/PanelThree";
import PanelFour from "components/Registration/PanelFour";
import PanelFive from "components/Registration/PanelFive";

const Register = () => {
  LoadingOverlay.propTypes = undefined;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [roleid, setRoleId] = useState(0);
  const [confirmationPassCode, setConfirmationPassCode] = useState("");
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [customerRegistrationFormData, setCustomerRegistrationFormData] =
    useState({
      ssn: "",
      phoneNumber: "",
      notificationMethod: "email",
    });

  useBreadcrumb([
    { name: t("CustomerRegister-home"), url: "/" },
    { name: t("CustomerRegister-register"), url: "#" },
  ]);
  return (
    <EverlakeLoadingOverlay isLoading={loading}>
      <div className="flex flex-col flex-grow">
        <div className="border border-transparent">
          <div className="container mb-8">
            <h1>{t("CustomerRegister-title")}</h1>
          </div>

          <div className="container mb-8">
            <div className="ui-form-registration">
              {/* <!-- Step 1 --> */}
              <div className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16">
                <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
                  <div className="flex justify-center items-center mt-6 mb-4">
                    <div
                      className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                        currentStep > 1
                          ? "bg-ui-blue"
                          : "bg-ui-gray-medium-dark"
                      } rounded-full overflow-hidden`}
                    >
                      {t("CustomerRegister-step-1-number")}
                    </div>
                  </div>
                  <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                </div>

                <div className="w-full flex-grow">
                  {/* <!-- Step 1 Card --> */}

                  <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                    <Disclosure as="div">
                      <>
                        <div className="flex justify-between items-start">
                          <h2 className="flex flex-grow font-body font-bold text-xl">
                            {t("CustomerRegister-step-1-title")}
                          </h2>
                          <div className="font-semibold text-sm whitespace-nowrap">
                            {t("CustomerRegister-step-1")}
                          </div>
                        </div>
                        {currentStep === 1 && (
                          <div className="flex justify-between items-start">
                            <h2 className="flex-grow font-body font-semibold text-xs italic text-ui-red">
                              {t("CustomerRegister-step-1-requiredText")}
                            </h2>
                          </div>
                        )}

                        <div>
                          {currentStep === 1 && (
                            <Disclosure.Panel static className="pb-2 text-sm">
                              <PanelOne
                                customerRegistrationFormData={
                                  customerRegistrationFormData
                                }
                                setCustomerRegistrationFormData={
                                  setCustomerRegistrationFormData
                                }
                                setCurrentStep={setCurrentStep}
                                setRoleId={setRoleId}
                                setLoading={setLoading}
                              />
                            </Disclosure.Panel>
                          )}
                        </div>
                      </>
                    </Disclosure>
                  </div>
                </div>
              </div>

              {/* <!-- Step 2 --> */}
              <div className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16">
                <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
                  <div className="flex justify-center items-center mt-3 mb-4">
                    <div
                      className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                        currentStep > 2
                          ? "bg-ui-blue"
                          : "bg-ui-gray-medium-dark"
                      } rounded-full overflow-hidden`}
                    >
                      {t("CustomerRegister-step-2-number")}
                    </div>
                  </div>
                  <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                </div>
                {/* <!-- Card 2 --> */}

                <div className="w-full flex-grow">
                  <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                    <Disclosure as="div">
                      <>
                        <div className="flex justify-between items-start">
                          <h2 className="flex flex-grow font-body font-bold text-xl">
                            {t("CustomerRegister-step-2-title")}
                          </h2>
                          <div className="font-semibold text-sm whitespace-nowrap">
                            {t("CustomerRegister-step-2")}
                          </div>
                        </div>
                        {currentStep === 2 && (
                          <div className="flex justify-between items-start">
                            <h2 className="flex-grow font-body font-semibold text-xs italic text-ui-red">
                              {t("CustomerRegister-step-2-requiredText")}
                            </h2>
                          </div>
                        )}

                        <div>
                          {currentStep === 2 && (
                            <Disclosure.Panel className="pb-2 text-sm" static>
                              <PanelTwo
                                customerRegistrationFormData={
                                  customerRegistrationFormData
                                }
                                setCustomerRegistrationFormData={
                                  setCustomerRegistrationFormData
                                }
                                setCurrentStep={setCurrentStep}
                                setLoading={setLoading}
                              />
                            </Disclosure.Panel>
                          )}
                        </div>
                      </>
                    </Disclosure>
                  </div>
                </div>
              </div>

              {/* <!-- Step 3 --> */}
              <div className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16">
                <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
                  <div className="flex justify-center items-center mt-3 mb-4">
                    <div
                      className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                        currentStep > 3
                          ? "bg-ui-blue"
                          : "bg-ui-gray-medium-dark"
                      } rounded-full overflow-hidden`}
                    >
                      {t("CustomerRegister-step-3-number")}
                    </div>
                  </div>
                  <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                </div>

                {/* Card 3 */}
                <div className="w-full flex-grow">
                  <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                    <Disclosure as="div">
                      <>
                        <div className="flex justify-between items-start">
                          <h2 className="flex flex-grow font-body font-bold text-xl">
                            {t("CustomerRegister-step-3-title")}
                          </h2>
                          <div className="font-semibold text-sm whitespace-nowrap">
                            {t("CustomerRegister-step-3")}
                          </div>
                        </div>

                        {currentStep === 3 && (
                          <div className="flex justify-between items-start">
                            <h2 className="flex-grow font-body font-semibold text-xs italic text-ui-red">
                              {t("CustomerRegister-step-2-requiredText")}
                            </h2>
                          </div>
                        )}

                        <div>
                          {currentStep === 3 && (
                            <Disclosure.Panel className="pb-2 text-sm" static>
                              <PanelThree
                                customerRegistrationFormData={
                                  customerRegistrationFormData
                                }
                                setCustomerRegistrationFormData={
                                  setCustomerRegistrationFormData
                                }
                                setCurrentStep={setCurrentStep}
                                setConfirmationPassCode={
                                  setConfirmationPassCode
                                }
                                setLoading={setLoading}
                              />
                            </Disclosure.Panel>
                          )}
                        </div>
                      </>
                    </Disclosure>
                  </div>
                </div>
              </div>
              {/* Step Four */}
              <div className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16">
                <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
                  <div className="flex justify-center items-center mt-3 mb-4">
                    <div
                      className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                        currentStep > 4
                          ? "bg-ui-blue"
                          : "bg-ui-gray-medium-dark"
                      } rounded-full overflow-hidden`}
                    >
                      {t("CustomerRegister-step-4-number")}
                    </div>
                  </div>
                  <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                </div>

                {/* Card 4 */}
                <div className="w-full flex-grow">
                  <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                    <Disclosure as="div">
                      <>
                        <div className="flex justify-between items-start">
                          <h2 className="flex flex-grow font-body font-bold text-xl">
                            {t("CustomerRegister-step-4-title")}
                          </h2>
                          <div className="font-semibold text-sm whitespace-nowrap">
                            {t("CustomerRegister-step-4")}
                          </div>
                        </div>

                        <div>
                          {currentStep === 4 && (
                            <Disclosure.Panel className="pb-2 text-sm" static>
                              <PanelFour
                                setCurrentStep={setCurrentStep}
                                isConsentChecked={isConsentChecked}
                                setIsConsentChecked={setIsConsentChecked}
                              />
                            </Disclosure.Panel>
                          )}
                        </div>
                      </>
                    </Disclosure>
                  </div>
                </div>
              </div>
              {/* Step Five */}
              <div className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16">
                <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
                  <div className="flex justify-center items-center mt-3 mb-4">
                    <div
                      className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                        currentStep > 5
                          ? "bg-ui-blue"
                          : "bg-ui-gray-medium-dark"
                      } rounded-full overflow-hidden`}
                    >
                      {t("CustomerRegister-step-5-number")}
                    </div>
                  </div>
                  <div className="flex h-full w-px bg-transparent mx-auto overflow-hidden"></div>
                </div>

                {/* Card 4 */}
                <div className="w-full flex-grow">
                  <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                    <Disclosure as="div">
                      <>
                        <div className="flex justify-between items-start">
                          <h2 className="flex flex-grow font-body font-bold text-xl">
                            {t("CustomerRegister-step-5-title")}
                          </h2>
                          <div className="font-semibold text-sm whitespace-nowrap">
                            {t("CustomerRegister-step-5")}
                          </div>
                        </div>
                        {currentStep === 5 && (
                          <div className="flex justify-between items-start">
                            <h2 className="flex-grow font-body font-semibold text-xs italic text-ui-red">
                              {t("CustomerRegister-step-2-requiredText")}
                            </h2>
                          </div>
                        )}

                        <div>
                          {currentStep === 5 && (
                            <Disclosure.Panel className="pb-2 text-sm" static>
                              <PanelFive
                                customerRegistrationFormData={
                                  customerRegistrationFormData
                                }
                                setCustomerRegistrationFormData={
                                  setCustomerRegistrationFormData
                                }
                                setCurrentStep={setCurrentStep}
                                setGeneratedPassCode={setConfirmationPassCode}
                                generatedPassCode={confirmationPassCode}
                                roleid={roleid}
                                setLoading={setLoading}
                              />
                            </Disclosure.Panel>
                          )}
                        </div>
                      </>
                    </Disclosure>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </EverlakeLoadingOverlay>
  );
};

export default Register;
